// Markdown.js — a small, dependency-free GitHub-flavored-Markdown renderer for AI assistant output.
//
// Why hand-rolled (not react-markdown): the AI assistant ("Aira") streams GFM (headings, **bold**,
// lists, > quotes, `code`, and | tables |); the chat UI previously rendered it as raw `whitespace-pre-wrap`
// text so users saw the literal markup. This renders it as proper, well-structured, ACCESSIBLE output
// using the design tokens — with zero new deps (no ESM/CRA build risk) and no `dangerouslySetInnerHTML`
// (every node is a React text node, so it is XSS-safe). Links are restricted to http(s)/mailto.
import React from "react";

// ---- inline: **bold**, *italic*/_italic_, `code`, [text](url) ------------------------------------
const SAFE_HREF = /^(https?:\/\/|mailto:|\/(?!\/))/i;
const LIST_ITEM = /^(\s*)([-*+]|\d+\.)\s+(.*)$/;

function renderInline(text, keyPrefix) {
  if (!text) return null;
  const INLINE = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|_[^_\n]+_|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  const out = [];
  let last = 0, m, i = 0;
  while ((m = INLINE.exec(text)) !== null) {
    const tok = m[0];
    // GFM: underscore emphasis NEVER applies inside a word (snake_case, `field_key`) — a `_…_`/`__…__`
    // candidate flanked by a word char or backtick stays literal text; resume after the opening char.
    if (tok[0] === "_") {
      const prev = m.index > 0 ? text[m.index - 1] : "";
      const next = text[m.index + tok.length] || "";
      if (/[\w`]/.test(prev) || /[\w`]/.test(next)) {
        INLINE.lastIndex = m.index + 1; // the skipped text flushes as part of the next gap
        continue;
      }
    }
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${keyPrefix}-i${i++}`;
    if (tok.startsWith("**") || tok.startsWith("__")) out.push(<strong key={k}>{renderInline(tok.slice(2, -2), k)}</strong>);
    else if (tok.startsWith("`")) out.push(<code key={k} className="px-1 py-0.5 rounded bg-ai/10 text-ink text-[0.9em] font-mono">{tok.slice(1, -1)}</code>);
    else if (tok.startsWith("[")) {
      const mm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok);
      const href = mm ? mm[2] : "";
      if (mm && SAFE_HREF.test(href)) {
        const external = /^https?:\/\//i.test(href);
        out.push(
          <a
            key={k}
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            className="text-ai underline underline-offset-2 decoration-ai/40 hover:decoration-ai rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {mm[1]}
            {external && (
              <>
                <svg aria-hidden="true" viewBox="0 0 24 24" className="inline-block w-3 h-3 ml-0.5 -translate-y-px" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17 17 7M9 7h8v8" /></svg>
                <span className="sr-only"> (opens in a new tab)</span>
              </>
            )}
          </a>
        );
      } else out.push(mm ? mm[1] : tok);
    } else out.push(<em key={k}>{renderInline(tok.slice(1, -1), k)}</em>); // *italic* / _italic_
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function splitRow(line) {
  return line.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());
}

function getListItem(line) {
  const m = LIST_ITEM.exec(line);
  if (!m) return null;
  const marker = m[2];
  return {
    indent: m[1].replace(/\t/g, "  ").length,
    ordered: /\d+\./.test(marker),
    start: parseInt(marker, 10) || 1,
    text: m[3],
  };
}

function parseList(lines, idx, indent, keyPrefix) {
  const first = getListItem(lines[idx]);
  const ordered = first.ordered;
  const items = [];
  let i = idx;

  while (i < lines.length) {
    const item = getListItem(lines[i]);
    if (!item || item.indent < indent) break;
    if (item.indent > indent) {
      if (!items.length) break;
      const parent = items[items.length - 1];
      const nested = parseList(lines, i, item.indent, `${keyPrefix}-${items.length - 1}-c${parent.children.length}`);
      parent.children.push(nested.node);
      i = nested.next;
      continue;
    }
    if (item.ordered !== ordered) break;
    items.push({ text: item.text, children: [] });
    i++;
    // GFM lazy continuation: plain lines directly under a list item (no blank line, not a new
    // block) belong to that item — docs authored with wrapped list items stay one <li>.
    while (
      i < lines.length && lines[i].trim() && !getListItem(lines[i]) &&
      !/^(#{1,6}\s|```|\s*>\s?)/.test(lines[i]) && !/^(\s*[-*_]){3,}\s*$/.test(lines[i]) &&
      !(lines[i].includes("|") && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]))
    ) {
      items[items.length - 1].text += " " + lines[i].trim();
      i++;
    }
  }

  const Tag = ordered ? "ol" : "ul";
  return {
    next: i,
    node: (
      <Tag key={keyPrefix} start={ordered && first.start !== 1 ? first.start : undefined} className={`${ordered ? "list-decimal" : "list-disc"} my-2 ml-4 space-y-0.5 text-ink-2`}>
        {items.map((it, x) => <li key={x}>{renderInline(it.text, `${keyPrefix}-${x}`)}{it.children}</li>)}
      </Tag>
    ),
  };
}

// ---- fenced code block: keyboard-scrollable region + always-reachable copy affordance -----------
function CodeBlock({ code }) {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try {
      await window.navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="relative group my-2">
      <pre tabIndex={0} className="p-3 pr-12 rounded-lg bg-ai/5 overflow-x-auto text-[0.8125rem] leading-relaxed font-mono text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"><code>{code}</code></pre>
      <button
        type="button"
        onClick={onCopy}
        aria-label={copied ? "Code copied" : "Copy code"}
        className="absolute top-1.5 right-1.5 inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-2 hover:text-ink hover:bg-muted opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 max-md:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {copied
          ? <svg aria-hidden="true" viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6 9 17l-5-5" /></svg>
          : <svg aria-hidden="true" viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>}
      </button>
    </div>
  );
}

// ---- block parser -------------------------------------------------------------------------------
function parseBlocks(content, { headingBase = 3 } = {}) {
  const src = String(content || "");
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  const key = () => `b${blocks.length}`;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }                                  // blank

    if (/^```/.test(line)) {                                              // fenced code
      const buf = []; i++;
      while (i < lines.length && !/^```/.test(lines[i])) { buf.push(lines[i]); i++; }
      i++; // closing fence
      blocks.push(<CodeBlock key={key()} code={buf.join("\n")} />);
      continue;
    }

    const h = /^(#{1,6})\s+(.*)$/.exec(line);                            // heading
    if (h) {
      if (!h[2].trim()) { i++; continue; }                              // skip malformed "### " — no empty <h>, no loop
      const depth = h[1].length;
      const lvl = Math.min(6, Math.max(1, headingBase + depth - 1));
      const Tag = `h${lvl}`;
      const sz = ["text-base", "text-sm", "text-sm", "text-sm", "text-sm", "text-sm"][depth - 1];
      blocks.push(<Tag key={key()} className={`font-heading font-semibold text-ink mt-3 first:mt-0 ${sz}`}>{renderInline(h[2], key())}</Tag>);
      i++; continue;
    }

    if (/^(\s*[-*_]){3,}\s*$/.test(line)) { blocks.push(<hr key={key()} className="my-3 border-line" />); i++; continue; } // hr

    // GFM table: a header row of pipes followed by a |---|---| separator
    if (line.includes("|") && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes("-")) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) { rows.push(splitRow(lines[i])); i++; }
      blocks.push(
        <div key={key()} tabIndex={0} className="my-2 overflow-x-auto rounded-lg border border-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          <table className="w-full text-sm">
            <caption className="sr-only">Table from assistant response</caption>
            <thead><tr className="bg-muted">{header.map((c, x) => <th key={x} scope="col" className="text-left px-3 py-1.5 font-semibold text-ink border-b border-line">{renderInline(c, `${key()}h${x}`)}</th>)}</tr></thead>
            <tbody>{rows.map((r, y) => <tr key={y} className="border-b border-line/60 last:border-0">{r.map((c, x) => <td key={x} className="px-3 py-1.5 text-ink">{renderInline(c, `${key()}r${y}c${x}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>);
      continue;
    }

    if (/^\s*>\s?/.test(line)) {                                         // blockquote
      const buf = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^\s*>\s?/, "")); i++; }
      blocks.push(<blockquote key={key()} className="my-2 pl-3 border-l-2 border-ai/40 text-ink-2">{renderInline(buf.join(" "), key())}</blockquote>);
      continue;
    }

    const list = getListItem(line);                                     // ordered / unordered list (nested-aware)
    if (list) {
      const parsed = parseList(lines, i, list.indent, key());
      blocks.push(parsed.node);
      i = parsed.next;
      continue;
    }

    // paragraph: gather consecutive plain lines
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|```|\s*>\s?|\s*[-*+]\s+|\s*\d+\.\s+)/.test(lines[i]) && !/^(\s*[-*_]){3,}\s*$/.test(lines[i])) {
      para.push(lines[i]); i++;
    }
    blocks.push(<p key={key()} className="my-1.5 leading-relaxed">{renderInline(para.join(" "), key())}</p>);
  }

  return blocks;
}

export function Markdown({ content, className, headingBase = 3 }) {
  const blocks = React.useMemo(() => parseBlocks(content, { headingBase }), [content, headingBase]);
  return <div className={className}>{blocks}</div>;
}

export default Markdown;